import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./CampaignCreatePage.css";
import "../components/MessagingButtons.css";
import "../components/MessagingPage.css";
import "../components/MessageBodyEditor.css";
import { useAuth } from "../../../hooks/useAuth";
import {
  useMessagingAudience,
  MessagingAudience,
} from "../../../hooks/useMessagingAudience";
import { useMessageTemplates } from "../../../hooks/useMessageTemplates";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { createCampaignAsync } from "../../../api/MessageApi";
import { Constants } from "../../../constants/constants";
import {
  CHANNEL_OPTIONS,
  CHANNEL_UNAVAILABLE_NOTE,
  ComposeState,
  MessageChannel,
  MessageType,
  MESSAGE_TYPE_LABELS,
  normalizeComposeChannel,
  SendCampaignResult,
  withBanglaContactLine,
} from "../../../models/MessageModels";
import ClientAudienceFilter from "../components/ClientAudienceFilter";
import ClientSelectableTable from "../components/ClientSelectableTable";
import MessageBodyEditor from "../components/MessageBodyEditor";
import CampaignResultPanel from "../components/CampaignResultPanel";
import Toast from "../../../components/Toast";
import MessagingUsageBar from "../../../components/MessagingUsageBar";
import { OrgForms } from "../../../config/forms";

const CampaignCreatePage = () => {
  const { user, hasPrivilege } = useAuth();
  const canCreateCampaigns = hasPrivilege(OrgForms.Messaging, "canCreate");
  const { entitlements, refresh: refreshEntitlements } = useEntitlements();
  const organizationId = user?.activeOrganization?.organizationId;
  const orgName = user?.activeOrganization?.name ?? "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialClientIds = (searchParams.get("clientIds") ?? "")
    .split(",")
    .filter(Boolean);
  const initialChannel = normalizeComposeChannel(
    Number(searchParams.get("channel") ?? MessageChannel.Sms),
  );
  const initialMessageType = Number(
    searchParams.get("messageType") ?? MessageType.Campaign,
  );

  const [audience, setAudience] = useState<MessagingAudience>("all");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const {
    rows,
    pagination,
    loading: audienceLoading,
    error,
    reload,
    setPage,
  } = useMessagingAudience(organizationId, audience, {
    search: appliedSearch,
  });
  const { templates, loading: templatesLoading } =
    useMessageTemplates(organizationId);

  const [name, setName] = useState("");
  const [compose, setCompose] = useState<ComposeState>(() => ({
    channel: normalizeComposeChannel(initialChannel),
    messageType: Number.isNaN(initialMessageType)
      ? MessageType.Campaign
      : initialMessageType,
    templateId: searchParams.get("templateId"),
    body: searchParams.get("body") ?? "",
  }));
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialClientIds),
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SendCampaignResult | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const showToast = (message: string, type: "success" | "error" | "info") =>
    setToast({ message, type });

  const changeAudience = (next: MessagingAudience) => {
    setAudience(next);
    setPage(1);
  };

  const submitSearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const previewRow =
    rows.find((row) => selected.has(row.clientId)) ?? rows[0] ?? null;
  const previewClient = previewRow
    ? { fullName: previewRow.fullName, phone: previewRow.phone }
    : null;

  const loading = audienceLoading || templatesLoading;

  const toggleClient = (clientId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const togglePage = (clientIds: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      clientIds.forEach((clientId) => {
        if (next.has(clientId)) next.delete(clientId);
        else next.add(clientId);
      });
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const hasEnoughRemaining = (count: number): boolean => {
    if (!entitlements || entitlements.monthlyMessageLimit < 0) return true;
    return entitlements.messagesRemaining >= count;
  };

  const limitReached =
    selected.size > 0 && !hasEnoughRemaining(selected.size);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId || selected.size === 0) return;
    if (!hasEnoughRemaining(selected.size)) {
      showToast(
        `Not enough message credit — you need ${selected.size}, only ${entitlements?.messagesRemaining ?? 0} remaining this month.`,
        "error",
      );
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const typeLabel =
      MESSAGE_TYPE_LABELS[compose.messageType as MessageType] ?? "Campaign";
    try {
      const res = await createCampaignAsync(token, {
        organizationId,
        channel: normalizeComposeChannel(compose.channel),
        messageType: compose.messageType,
        templateId: compose.templateId,
        name: name.trim() || `${typeLabel} — ${new Date().toLocaleString()}`,
        body: compose.body ? withBanglaContactLine(compose.body) : null,
        clientIds: [...selected],
      });
      if (res.success && res.result) {
        await refreshEntitlements();
        setResult(res.result);
        showToast(
          `Campaign created — ${res.result.successCount} sent, ${res.result.failCount} failed`,
          "success",
        );
      } else {
        showToast(res.message || "Failed to create campaign", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreateCampaigns) {
    return (
      <div className="msg-page">
        <div className="msg-page__header">
          <div>
            <h2>New Campaign</h2>
          </div>
        </div>
        <div className="msg-table-empty">
          You don't have permission to create campaigns.
        </div>
      </div>
    );
  }

  return (
    <div className="msg-page">
      <div className="msg-page__header">
        <div>
          <h2>New Campaign</h2>
          <p>
            Send a bulk message to a filtered group of clients at{" "}
            {orgName || "your organization"}.
          </p>
        </div>
      </div>

      <MessagingUsageBar entitlements={entitlements} />

      {result ? (
        <div className="msg-create__result">
          <CampaignResultPanel
            result={result}
            onViewCampaign={(id) => navigate(`/messaging/campaigns/${id}`)}
          />
          <button
            className="msg-btn msg-btn--secondary"
            onClick={() => {
              setResult(null);
              setSelected(new Set());
              setName("");
            }}
          >
            Start over
          </button>
        </div>
      ) : (
        <form className="msg-create" onSubmit={handleSubmit}>
          <section className="msg-create__audience">
            <ClientAudienceFilter
              mode={audience}
              onModeChange={changeAudience}
              search={searchInput}
              onSearchChange={setSearchInput}
              onSearchSubmit={submitSearch}
              messageType={compose.messageType}
              onMessageTypeChange={(value) =>
                setCompose((prev) => ({ ...prev, messageType: value }))
              }
              modes={["all", "pending", "due"]}
              disabled={loading}
            />
            <ClientSelectableTable
              rows={rows}
              totalCount={pagination.totalCount}
              page={pagination.page}
              pageSize={pagination.pageSize}
              onPageChange={setPage}
              selectedIds={selected}
              sentStatus={{}}
              loading={loading}
              error={error}
              onRetry={reload}
              onToggle={toggleClient}
              onSelectPage={togglePage}
              onClearSelection={clearSelection}
            />
          </section>

          <aside className="msg-create__form">
            <div className="msg-campaign-form">
              <h3 className="msg-campaign-form__title">Campaign details</h3>

              <label className="msg-editor__field">
                <span className="msg-editor__label">Name</span>
                <input
                  type="text"
                  value={name}
                  placeholder="e.g. August follow-up offer"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <label className="msg-editor__field">
                <span className="msg-editor__label">Channel</span>
                <select
                  value={String(normalizeComposeChannel(compose.channel))}
                  onChange={(e) =>
                    setCompose({ ...compose, channel: Number(e.target.value) })
                  }
                >
                  {CHANNEL_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="msg-editor__hint">
                  {CHANNEL_UNAVAILABLE_NOTE}
                </span>
              </label>

              <label className="msg-editor__field">
                <span className="msg-editor__label">Message type</span>
                <select
                  value={String(compose.messageType)}
                  onChange={(e) =>
                    setCompose({
                      ...compose,
                      messageType: Number(e.target.value),
                    })
                  }
                >
                  {Object.entries(MESSAGE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <MessageBodyEditor
                compose={compose}
                templates={templates}
                orgName={orgName}
                previewClient={previewClient}
                onChange={setCompose}
              />

              <div className="msg-campaign-form__footer">
                <span className="msg-campaign-form__count">
                  {selected.size} client{selected.size === 1 ? "" : "s"} selected
                </span>
                {limitReached && (
                  <span className="msg-campaign-form__limit" role="alert">
                    Not enough monthly message credit for {selected.size} clients.
                  </span>
                )}
                <button
                  className="msg-btn msg-btn--primary"
                  disabled={
                    submitting ||
                    selected.size === 0 ||
                    !compose.body.trim() ||
                    limitReached
                  }
                >
                  {submitting
                    ? "Creating…"
                    : `Create campaign (${selected.size})`}
                </button>
              </div>
            </div>
          </aside>
        </form>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CampaignCreatePage;
